import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Shield,
  ChevronDownIcon,
  Phone,
  Mail,
  User,
  Settings,
  ShieldCheck,
  Building,
  MapPin,
} from "lucide-react";
import userService from "../services/userService";
import ModalUsuario from "../components/Modales/ModalUsuario";
import { useNotifications } from "../contexts/NotificationContext";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import ConfirmDialog from "../components/Modales/ConfirmDialog";
import { Tooltip } from "../components/ui/Tooltip";
import { Button, Select } from "@headlessui/react";
import clsx from "clsx";
import "../styles/pages/Usuarios.css";

const getAvatarColor = (name) => {
  const colors = [
    "bg-blue-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-teal-500",
    "bg-emerald-500",
    "bg-amber-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name) => {
  return name
    ? name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
    : "??";
};

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [rolFiltro, setRolFiltro] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [usuarioAEliminar, setUsuarioAEliminar] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { showSuccess, showError } = useNotifications();

  const cargarUsuarios = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await userService.getUsuarios();
      if (res.success) {
        const lista = Array.isArray(res.usuarios) ? res.usuarios : [];
        setUsuarios(lista);
        setFiltered(lista);
      } else {
        setUsuarios([]);
        setFiltered([]);
        showError(res.error || "Error al cargar usuarios");
      }
    } catch (e) {
      setUsuarios([]);
      setFiltered([]);
      showError("Error inesperado al cargar usuarios");
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    cargarUsuarios();
  }, [cargarUsuarios]);

  // Filtrado
  useEffect(() => {
    let data = [...usuarios];
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(
        (u) =>
          u.nombre?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.contacto?.toLowerCase().includes(q)
      );
    }
    if (rolFiltro) {
      data = data.filter((u) => {
        const r = Array.isArray(u.rol) ? u.rol[0] : u.rol;
        return r === rolFiltro;
      });
    }
    setFiltered(data);
  }, [search, rolFiltro, usuarios]);

  const handleCrear = () => {
    setUsuarioSeleccionado(null);
    setModalOpen(true);
  };
  const handleEditar = (u) => {
    setUsuarioSeleccionado(u);
    setModalOpen(true);
  };

  const handleGuardar = async (datos) => {
    setIsSubmitting(true);
    try {
      let res;
      const payload = { ...datos };
      res = usuarioSeleccionado
        ? await userService.actualizarUsuario(usuarioSeleccionado.id, payload)
        : await userService.crearUsuario(payload);
      if (res.success) {
        showSuccess(res.message || "Operación exitosa");
        setModalOpen(false);
        cargarUsuarios();
      } else {
        showError(res.error || "Error al guardar el usuario");
      }
    } catch (e) {
      showError("Error inesperado al guardar usuario");
    } finally {
      setIsSubmitting(false);
    }
  };

  const solicitarDesactivacion = (usuario) => {
    setUsuarioAEliminar(usuario);
    setConfirmOpen(true);
  };

  const confirmarDesactivacion = async () => {
    if (!usuarioAEliminar) return;
    setIsDeleting(true);
    try {
      const res = await userService.desactivarUsuario(usuarioAEliminar.id);
      if (res.success) {
        showSuccess(res.message || "Usuario desactivado");
        setConfirmOpen(false);
        setUsuarioAEliminar(null);
        cargarUsuarios();
      } else {
        showError(res.error || "Error al desactivar");
      }
    } catch (e) {
      showError("Error inesperado al desactivar usuario");
    } finally {
      setIsDeleting(false);
    }
  };

  const getRoleIcon = (rol) => {
    switch (rol) {
      case "admin":
        return <ShieldCheck className="w-3.5 h-3.5" />;
      case "operador":
        return <Settings className="w-3.5 h-3.5" />;
      case "lugar":
        return <Building className="w-3.5 h-3.5" />;
      default:
        return <User className="w-3.5 h-3.5" />;
    }
  };

  if (isLoading) {
    return (
      <div className="usuarios-container relative p-4 md:p-8 min-h-screen bg-gray-50 dark:bg-[#121212] rounded-3xl md:rounded-[2.5rem] shadow-xl overflow-hidden animate-pulse">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-[#206a73]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-64 h-64 bg-[#206a73]/5 rounded-full blur-3xl pointer-events-none" />

        <header className="relative mb-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="p-2 bg-[#206a73]/10 dark:bg-[#206a73]/20 rounded-2xl">
                  <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
                </div>
                <div className="h-7 w-48 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
              </div>
              <div className="h-4 w-72 bg-gray-200 dark:bg-gray-700 rounded-md ml-1 mt-2"></div>
            </div>
            
            <div className="h-10 w-36 bg-gray-200 dark:bg-gray-700 rounded-2xl"></div>
          </div>

          {/* Search and Filters Bar */}
          <div className="mt-8 flex flex-col lg:flex-row items-center gap-4 p-4 bg-white dark:bg-[#1e1e1e] rounded-3xl shadow-sm border border-gray-100 dark:border-gray-800">
            <div className="flex-1 w-full h-6 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
            <div className="h-8 w-[1px] bg-gray-100 dark:bg-gray-800 hidden lg:block" />
            <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded-md w-full lg:w-auto"></div>
          </div>
        </header>

        <main>
          {/* Vista de escritorio (Tabla) */}
          <div className="hidden lg:block overflow-x-auto pb-4">
            <table className="custom-table min-w-full border-separate">
              <thead>
                <tr className="text-gray-400 dark:text-gray-500 text-[10px] font-bold uppercase tracking-[0.15em]">
                  <th className="px-6 py-4 text-left font-semibold">Usuario</th>
                  <th className="px-6 py-4 text-left font-semibold">Credenciales</th>
                  <th className="px-6 py-4 text-left font-semibold">Dirección</th>
                  <th className="px-6 py-4 text-left font-semibold">Permisos</th>
                  <th className="px-6 py-4 text-left font-semibold">Contacto</th>
                  <th className="px-6 py-4 text-right font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y-8 divide-transparent">
                {[...Array(5)].map((_, i) => (
                  <tr key={i} className="user-row bg-white dark:bg-[#1e1e1e] shadow-sm">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                        <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-2">
                        <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                        <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Vista de móvil/tablet (Tarjetas) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:hidden">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-[#1e1e1e] p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 flex flex-col gap-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
                    <div className="flex flex-col gap-2">
                      <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
                      <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                  </div>
                </div>
                <div className="space-y-3 border-t border-gray-100 dark:border-gray-800 pt-3">
                  <div className="h-3 w-48 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
                  <div className="h-3 w-36 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
                  <div className="h-3 w-40 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
                </div>
              </div>
            ))}
          </div>
        </main>

        <footer className="mt-8 flex items-center justify-between px-2">
          <div className="h-4 w-36 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
        </footer>
      </div>
    );
  }

  return (
    <div className="usuarios-container relative p-4 md:p-8 min-h-screen bg-gray-50 dark:bg-[#121212] rounded-3xl md:rounded-[2.5rem] shadow-xl overflow-hidden">
      
      {/* Background decoration */}
      <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-[#206a73]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-64 h-64 bg-[#206a73]/5 rounded-full blur-3xl pointer-events-none" />

      <header className="relative mb-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 bg-[#206a73]/10 dark:bg-[#206a73]/20 rounded-2xl text-[#206a73]">
                <Shield className="w-6 h-6" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                Gestión de Usuarios
              </h1>
            </div>
            <p className="text-gray-500 dark:text-gray-400 font-medium ml-1 text-sm">
              Control total sobre los accesos y permisos del sistema
            </p>
          </div>
          
          <Tooltip content="Crear nuevo usuario">
            <button
              onClick={handleCrear}
              className="action-btn flex items-center justify-center gap-2 px-6 py-2.5 bg-[#206a73] hover:bg-[#155059] text-white font-bold rounded-2xl shadow-lg shadow-[#206a73]/20 transition-all active:scale-95 text-sm"
            >
              <Plus className="w-4 h-4 stroke-[3]" />
              <span className="hidden sm:inline">Nuevo Usuario</span>
            </button>
          </Tooltip>
        </div>

        {/* Search and Filters Bar */}
        <div className="mt-8 flex flex-col lg:flex-row items-center gap-4 p-2 bg-white dark:bg-[#1e1e1e] rounded-3xl shadow-sm border border-gray-100 dark:border-gray-800 transition-all focus-within:ring-2 focus-within:ring-[#206a73]/20 focus-within:border-[#206a73]/50">
          <div className="search-input-wrapper flex-1 w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, email o contacto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-2.5 bg-transparent border-none focus:ring-0 focus:outline-none text-gray-700 dark:text-gray-200 placeholder:text-gray-400 font-medium text-sm"
            />
          </div>
          
          <div className="h-8 w-[1px] bg-gray-100 dark:bg-gray-800 hidden lg:block" />

          <div className="relative min-w-[200px] w-full lg:w-auto">
            <Select
              value={rolFiltro}
              onChange={(e) => setRolFiltro(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 bg-transparent border-none focus:ring-0 focus:outline-none text-gray-700 dark:text-gray-200 font-semibold cursor-pointer appearance-none text-sm"
            >
              <option value="" className="bg-white dark:bg-[#1e1e1e]">Todos los roles</option>
              <option value="admin" className="bg-white dark:bg-[#1e1e1e]">Administrador</option>
              <option value="operador" className="bg-white dark:bg-[#1e1e1e]">Operador</option>
              <option value="lugar" className="bg-white dark:bg-[#1e1e1e]">Lugar</option>
            </Select>
            <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </header>

      <main>
        {!isLoading && filtered.length === 0 ? (
          <div className="text-center py-24 bg-white/50 dark:bg-black/10 rounded-[2rem] border-2 border-dashed border-gray-200 dark:border-gray-800">
            <div className="mx-auto w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-3xl flex items-center justify-center mb-4">
              <User className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              {search ? "Sin resultados" : "No hay usuarios registrados"}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1 max-w-xs mx-auto text-sm">
              {search 
                ? "Prueba con otros términos de búsqueda o limpia los filtros." 
                : "Comienza añadiendo un nuevo usuario al sistema."}
            </p>
          </div>
        ) : (
          !isLoading && (
            <>
              {/* Vista de escritorio (Tabla) */}
              <div className="hidden lg:block overflow-x-auto pb-4">
                <table className="custom-table min-w-full border-separate">
                  <thead>
                    <tr className="text-gray-400 dark:text-gray-500 text-[10px] font-bold uppercase tracking-[0.15em]">
                      <th className="px-6 py-4 text-left font-semibold">Usuario</th>
                      <th className="px-6 py-4 text-left font-semibold">Credenciales</th>
                      <th className="px-6 py-4 text-left font-semibold">Dirección</th>
                      <th className="px-6 py-4 text-left font-semibold">Permisos</th>
                      <th className="px-6 py-4 text-left font-semibold">Contacto</th>
                      <th className="px-6 py-4 text-right font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y-8 divide-transparent">
                    {filtered.map((u) => {
                      const rol = Array.isArray(u.rol) ? u.rol[0] : u.rol;
                      return (
                        <tr key={u.id} className="user-row bg-white dark:bg-[#1e1e1e] group shadow-sm">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className={clsx("avatar-circle !w-9 !h-9 !text-xs", getAvatarColor(u.nombre))}>
                                {getInitials(u.nombre)}
                              </div>
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-[#206a73] transition-colors">
                                  {u.nombre}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 font-medium">
                              <div className="p-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <Mail className="w-3.5 h-3.5 text-gray-400" />
                              </div>
                              {u.email}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 font-medium max-w-[180px] truncate">
                              <div className="p-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                              </div>
                              {u.direccion || <span className="text-gray-300 dark:text-gray-600 italic">No registrada</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={clsx(
                              "role-badge !text-[10px] !py-1",
                              rol === "admin" 
                                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                                : "bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 border border-teal-100 dark:border-teal-800"
                            )}>
                              {getRoleIcon(rol)}
                              <span className="capitalize">{rol}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 font-medium">
                              <div className="p-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <Phone className="w-3.5 h-3.5 text-gray-400" />
                              </div>
                              {u.contacto || <span className="text-gray-300 dark:text-gray-600 italic">No registrado</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex justify-end items-center gap-2">
                              <Tooltip content="Editar usuario">
                                <button
                                  onClick={() => handleEditar(u)}
                                  className="action-btn p-2 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-[#206a73]/10 hover:text-[#206a73] rounded-xl transition-all"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                              </Tooltip>
                              <Tooltip content="Desactivar acceso" position="left">
                                <button
                                  onClick={() => solicitarDesactivacion(u)}
                                  className="action-btn p-2 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 rounded-xl transition-all"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Vista de móvil/tablet (Tarjetas) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:hidden">
                {filtered.map((u) => {
                  const rol = Array.isArray(u.rol) ? u.rol[0] : u.rol;
                  return (
                    <div
                      key={u.id}
                      className="bg-white dark:bg-[#1e1e1e] p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 flex flex-col gap-4"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={clsx("avatar-circle !w-10 !h-10 !text-xs flex-shrink-0", getAvatarColor(u.nombre))}>
                            {getInitials(u.nombre)}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-bold text-gray-900 dark:text-white truncate">
                              {u.nombre}
                            </span>
                            <span className={clsx(
                              "role-badge mt-1 !text-[9px] !py-0.5 !px-2 w-fit flex-shrink-0",
                              rol === "admin" 
                                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                                : "bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 border border-teal-100 dark:border-teal-800"
                            )}>
                              {getRoleIcon(rol)}
                              <span className="capitalize">{rol}</span>
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-1.5 flex-shrink-0">
                          <Tooltip content="Editar usuario">
                            <button
                              onClick={() => handleEditar(u)}
                              className="action-btn p-2 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-[#206a73]/10 hover:text-[#206a73] rounded-xl transition-all"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Desactivar acceso" position="left">
                            <button
                              onClick={() => solicitarDesactivacion(u)}
                              className="action-btn p-2 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 rounded-xl transition-all"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>

                      <div className="space-y-2 border-t border-gray-100 dark:border-gray-800 pt-3">
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 font-medium">
                          <div className="p-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg flex-shrink-0">
                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                          </div>
                          <span className="truncate">{u.email}</span>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 font-medium">
                          <div className="p-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg flex-shrink-0">
                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                          </div>
                          <span className="truncate">
                            {u.direccion || <span className="text-gray-300 dark:text-gray-600 italic">No registrada</span>}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 font-medium">
                          <div className="p-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg flex-shrink-0">
                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                          </div>
                          <span className="truncate">
                            {u.contacto || <span className="text-gray-300 dark:text-gray-600 italic">No registrado</span>}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )
        )}
      </main>

      <footer className="mt-8 flex items-center justify-between px-2">
        <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
          Mostrando <span className="text-gray-900 dark:text-gray-200">{filtered.length}</span> de <span className="text-gray-900 dark:text-gray-200">{usuarios.length}</span> registros
        </div>
        <div className="flex gap-2">
          {/* Pagination could go here in the future */}
        </div>
      </footer>

      <ModalUsuario
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        usuario={usuarioSeleccionado}
        onGuardar={handleGuardar}
        isLoading={isSubmitting}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Desactivar usuario"
        message={
          <span className="text-gray-600 dark:text-gray-300">
            ¿Estás seguro de que deseas desactivar al usuario{" "}
            <strong className="text-gray-900 dark:text-white">{usuarioAEliminar?.nombre}</strong>? 
            Podrás reactivarlo en cualquier momento desde el panel de control.
          </span>
        }
        confirmLabel="Sí, desactivar acceso"
        cancelLabel="Mantener activo"
        variant="danger"
        loading={isDeleting}
        onClose={() => {
          if (!isDeleting) {
            setConfirmOpen(false);
            setUsuarioAEliminar(null);
          }
        }}
        onConfirm={confirmarDesactivacion}
      />
    </div>
  );
}
